import { useCallback, useEffect, useRef, useState } from 'react'
import type { CanvasPanelState } from '../ProjectCanvas.types'
import {
  DRAG_THRESHOLD,
  RESIZE_ANIMATION_MS,
  trackSplitDrag,
  commitSplitDrag,
  type SplitContainerRect,
  type SplitDragResult,
} from '../utils/canvasSplitDrag'

/**
 * Die Klasse, die für die Dauer der Geste am `documentElement` hängt.
 *
 * Sie erledigt drei Dinge, die alle NICHT am Handle selbst zu lösen sind
 * (siehe `globals.scss`): den Zeiger `col-resize` halten, auch wenn er das
 * 16px schmale Handle längst verlassen hat (Pointer Capture leitet Ereignisse
 * um, nicht die Zeigerdarstellung); das Markieren von Text unterbinden,
 * während man über die Aufgabenliste zieht; und die Übergänge von Panel,
 * Listenspalte und Breadcrumb stilllegen, damit sie der Hand folgen statt ihr
 * 250ms hinterherzugleiten.
 */
const RESIZING_CLASS = 'canvas-resizing'

/**
 * Die Klasse, die zusätzlich sagt: Die Breite FOLGT gerade dem Zeiger, die
 * Übergänge bleiben still. Sie hängt fast die ganze Geste über — außer für die
 * Dauer eines Zustandswechsels.
 *
 * Getrennt von `RESIZING_CLASS`, weil Zeiger und Textmarkierung durchgehend
 * gelten müssen, das Stilllegen der Übergänge aber nicht.
 */
const TRACKING_CLASS = 'canvas-resizing-tracking'

/**
 * Wie lange nach einem Größenwechsel NICHT hart nachgesetzt wird, damit der
 * Übergang laufen kann.
 *
 * Beim Zuklappen nach links braucht es das streng genommen nicht — dort
 * verschwindet der `--split`-Modifier und die Stilllegungs-Regel greift von
 * selbst nicht mehr. Beim Öffnen aus `maximized` und beim Schließen nach rechts
 * schon: Dort bleibt (bzw. kommt) `--split`, und die Regel legte den Übergang
 * im selben Moment wieder still, in dem er anfangen soll.
 *
 * Ausgesetzt wird deshalb bei JEDEM Wechsel — eine Regel statt dreier
 * Sonderfälle, und in den Fällen, die es nicht brauchen, schadet es nichts.
 */
const SETTLE_MS = RESIZE_ANIMATION_MS

export interface UseCanvasSplitDragOptions {
  /**
   * Die Zeile, in der Aufgabenliste und Canvas nebeneinander liegen. Als
   * Funktion, weil sie erst beim `pointerdown` gemessen werden darf — vorher
   * kann sie sich noch ändern.
   */
  getRect: () => SplitContainerRect | null
  /** Die Größe, aus der heraus gezogen wird. */
  getState: () => CanvasPanelState
  /**
   * Die kleinste Canvas-Breite, auf die die Hand ihn ziehen kann: die
   * Streifenbreite des **Canvas-Anrisses**, wenn Elemente liegen, sonst 0.
   * Beim `pointerdown` gefragt, gilt für die ganze Geste.
   */
  getFloor?: () => number
  /** Bei jeder Bewegung ab der Schwelle. Nur Ansicht, nichts schreiben. */
  onChange: (result: SplitDragResult) => void
  /**
   * Beim Loslassen. Hier gehört der eine Schreibvorgang hin — mit dem
   * Ergebnis von `commitSplitDrag`, nicht dem letzten Verfolgten: Erst hier
   * darf der Canvas auf `closed` kippen.
   */
  onCommit: (result: SplitDragResult) => void
}

export interface CanvasSplitDragHandle {
  /**
   * Ob der Griff gerade angefasst ist — ab dem `pointerdown`, nicht erst ab
   * der Bewegungsschwelle.
   *
   * Die Schwelle schützt SCHREIBVORGÄNGE, nicht das Aussehen: Wer zugreift,
   * hat zugegriffen, und der Griff soll das im selben Moment zeigen. Erst zu
   * antworten, wenn sich der Zeiger schon 3px bewegt hat, ließ das Zufassen
   * ins Leere laufen.
   */
  pressed: boolean
  /** Direkt als `onPointerDown` verwendbar. */
  startDrag: (event: React.PointerEvent) => void
}

/**
 * Die Zeiger-Geste am **Drag Handle**: die Grenze zwischen **Aufgabenliste**
 * und **Projekt-Canvas** verschieben, und an den Rändern zwischen **geteilt**
 * und **maximiert** umschalten.
 *
 * Baugleich zu `useGroupDrag` — dieselbe Hausform, aus denselben Gründen:
 * `preventDefault` + `stopPropagation` (sonst zieht React Flows Pane einen
 * Auswahlrahmen auf), `setPointerCapture` (das Handle ist 16px breit, der
 * Zeiger verlässt es sofort), Optionen im Ref (die Handler sollen stabil
 * bleiben), `pointercancel` neben `pointerup`, und gerechnet wird IMMER aus
 * der aktuellen Zeigerlage, nie aus dem vorigen Ergebnis.
 *
 * Zwei Dinge kommen hinzu, die `useGroupDrag` nicht braucht:
 *
 * 1. **Die Schwelle trennt Melden von Anfassen.** Ab dem `pointerdown` gilt
 *    `pressed` und die Wurzel-Klasse hängt — der Griff sieht zugefasst aus und
 *    der Zeiger stimmt überall. GEMELDET wird aber erst ab 3px Bewegung: Sonst
 *    schriebe schon ein Klick ein Verhältnis, das sich nur um Bruchteile vom
 *    bisherigen unterscheidet, und der Doppelklick zum Zurücksetzen erzeugte
 *    drei Schreibvorgänge statt einem.
 * 2. **Die Größe wandert MIT der Geste.** Wer aus `maximized` herauszieht, ist
 *    ab dem Umschalten in `split` — und die Kollaps-Schwelle gilt ab da für
 *    `split`. Würde stattdessen immer aus der Ausgangsgröße gerechnet, klappte
 *    ein Zug, der erst öffnet und dann wieder zurückgeht, an der Mindestbreite
 *    der Liste sofort zu; die Hysterese aus `trackSplitDrag` käme nie zum Tragen.
 * 3. **Verfolgen und Festschreiben sind zwei Fragen.** Im Zug folgt die Kante
 *    der Hand (`trackSplitDrag`) — aus dem Anriss ab der Streifenbreite, nach
 *    rechts bis auf sie hinunter, ohne dass der Canvas dabei kippt. Erst das
 *    Loslassen entscheidet (`commitSplitDrag`), ob daraus `closed` wird.
 * 4. **Die Zeile wird je Bild nachgemessen, nicht nur je Bewegung.** Beim
 *    Öffnen aus dem Anriss fährt die Navigation weg und die Zeile wächst 250ms
 *    lang — bei stehender Hand käme keine Bewegung, das Verhältnis bliebe
 *    gegen die alte Breite gerechnet, und die Kante liefe der Hand um bis zu
 *    ~30px davon (gemessen im E2E). Ein `requestAnimationFrame`-Takt prüft
 *    deshalb je Bild, ob die Zeile sich geändert hat, und rechnet dann die
 *    letzte Zeigerlage neu. Gerechnet wird nur bei ÄNDERUNG — steht alles,
 *    kostet der Takt eine Messung je Bild und sonst nichts.
 *
 * @example
 * const { pressed, startDrag } = useCanvasSplitDrag({ getRect, getState, onChange, onCommit })
 * <div onPointerDown={startDrag} />
 */
export function useCanvasSplitDrag(options: UseCanvasSplitDragOptions): CanvasSplitDragHandle {
  const [pressed, setPressed] = useState(false)

  const optionsRef = useRef(options)
  optionsRef.current = options

  // Ob DIESE Instanz die Klasse gesetzt hat. Ohne das räumte ein Unmount sie
  // auch dann ab, wenn gar nicht gezogen wurde.
  const ownsClassRef = useRef(false)

  // Läuft, während ein Zustandswechsel ausanimiert.
  const settleTimerRef = useRef<number | null>(null)
  // Der Bild-Takt der Geste (Kopfkommentar, Punkt 4).
  const frameRef = useRef<number | null>(null)

  const stopFollowing = useCallback(() => {
    if (frameRef.current === null) return
    cancelAnimationFrame(frameRef.current)
    frameRef.current = null
  }, [])

  const clearSettleTimer = useCallback(() => {
    if (settleTimerRef.current === null) return
    window.clearTimeout(settleTimerRef.current)
    settleTimerRef.current = null
  }, [])

  const releaseClass = useCallback(() => {
    clearSettleTimer()
    stopFollowing()
    if (!ownsClassRef.current) return
    ownsClassRef.current = false
    document.documentElement.classList.remove(RESIZING_CLASS)
    document.documentElement.classList.remove(TRACKING_CLASS)
  }, [clearSettleTimer, stopFollowing])

  /**
   * Das harte Nachsetzen kurz aussetzen, damit ein Zustandswechsel dieselbe
   * Animation bekommt wie der Knopf oben rechts.
   *
   * Bewegt sich der Zeiger in dieser Zeit weiter, folgt die Kante ihm
   * geglättet statt hart — genau das macht das Hereinfahren der Liste flüssig.
   * Danach greift wieder die Verfolgung, sonst hinge die Kante dauerhaft eine
   * Viertelsekunde hinter der Hand.
   */
  const settle = useCallback(() => {
    document.documentElement.classList.remove(TRACKING_CLASS)
    clearSettleTimer()
    settleTimerRef.current = window.setTimeout(() => {
      settleTimerRef.current = null
      // Nur wieder scharf stellen, wenn die Geste überhaupt noch läuft.
      if (ownsClassRef.current) document.documentElement.classList.add(TRACKING_CLASS)
    }, SETTLE_MS)
  }, [clearSettleTimer])

  // Ein Unmount mitten im Zug — Projektwechsel, Tab zu — darf die Klasse nicht
  // am Dokument stehen lassen. Sie hinge sonst für den Rest der Sitzung dort
  // und legte jeden Übergang der Seite still.
  useEffect(() => releaseClass, [releaseClass])

  const startDrag = useCallback((event: React.PointerEvent) => {
    // Nicht an React Flow weiterreichen: Sonst startete die Pane einen
    // Auswahlrahmen unter dem Handle.
    event.preventDefault()
    event.stopPropagation()

    const initialRect = optionsRef.current.getRect()
    if (!initialRect || initialRect.width <= 0) return
    // Wandert mit der Geste (Kopfkommentar, Punkt 3) — bei jeder Bewegung neu.
    let rect: SplitContainerRect = initialRect
    const floor = optionsRef.current.getFloor?.() ?? 0

    const target = event.currentTarget as HTMLElement
    target.setPointerCapture?.(event.pointerId)

    // Sofort, nicht erst ab der Schwelle: Der Griff färbt sich (Figma
    // `Pressed`), der Zeiger bleibt `col-resize`, auch wenn er den Griff gleich
    // verlässt, und die Aufgabenliste unter der Bahn markiert keinen Text.
    setPressed(true)
    ownsClassRef.current = true
    document.documentElement.classList.add(RESIZING_CLASS)
    document.documentElement.classList.add(TRACKING_CLASS)

    const originX = event.clientX
    let past = false
    // Wandert mit der Geste (siehe Kopfkommentar, Punkt 2).
    let state = optionsRef.current.getState()
    let last: SplitDragResult | null = null
    // Die letzte Zeigerlage — der Bild-Takt rechnet sie neu, wenn die Zeile
    // sich unter ihr ändert (Kopfkommentar, Punkt 4).
    let lastX: number | null = null

    /** Die Kante zu dieser Zeigerlage legen — gegen die Zeile von JETZT. */
    const apply = (clientX: number) => {
      // Neu messen (Kopfkommentar, Punkt 3): Die Zeile wächst, während die
      // Navigation weicht. Eine Messung ohne Breite (Tab gerade weg) behält
      // die letzte.
      rect = optionsRef.current.getRect() ?? rect
      if (rect.width <= 0) return

      const result = trackSplitDrag(state, clientX, rect, floor)

      // Ein Wechsel zur oder von der MAXIMIERTEN Seite springt in der Breite
      // (die Liste kommt oder geht als Ganzes) und bekommt seinen Übergang —
      // dieselbe Animation wie die Knöpfe oben rechts (siehe `SETTLE_MS`).
      // `closed → split` dagegen ist stetig: Die Breite folgt ab dem ersten
      // Pixel der Hand, da gibt es nichts zu animieren — und ein Aussetzen
      // ließe die Kante 250ms hinter der Hand hergleiten.
      if (result.state !== state && (result.state === 'maximized' || state === 'maximized')) settle()

      state = result.state
      last = result
      optionsRef.current.onChange(result)
    }

    const handleMove = (moveEvent: PointerEvent) => {
      if (!past) {
        // Waagerecht gemessen: Ein Doppelklick wackelt gern ein paar Pixel nach
        // oben oder unten, und das ist keine Breitenänderung.
        if (Math.abs(moveEvent.clientX - originX) < DRAG_THRESHOLD) return
        past = true
      }
      lastX = moveEvent.clientX
      apply(lastX)
    }

    // Der Bild-Takt (Kopfkommentar, Punkt 4): Hat sich die Zeile geändert, ohne
    // dass die Hand sich bewegt hat, gilt die letzte Zeigerlage gegen die neue
    // Zeile. Nur bei Änderung — sonst kostet er eine Messung je Bild.
    const follow = () => {
      frameRef.current = requestAnimationFrame(follow)
      if (lastX === null) return
      const fresh = optionsRef.current.getRect()
      if (!fresh || fresh.width <= 0) return
      if (fresh.left === rect.left && fresh.width === rect.width) return
      apply(lastX)
    }
    stopFollowing()
    if (typeof requestAnimationFrame === 'function') frameRef.current = requestAnimationFrame(follow)

    const finish = () => {
      target.releasePointerCapture?.(event.pointerId)
      target.removeEventListener('pointermove', handleMove)
      target.removeEventListener('pointerup', finish)
      target.removeEventListener('pointercancel', finish)
      releaseClass()
      setPressed(false)
      // Ohne Schwelle war es ein Klick — dann gab es auch nichts zu ändern.
      // Sonst entscheidet erst hier, was aus dem Verfolgten wird.
      if (past && last) optionsRef.current.onCommit(commitSplitDrag(last, rect))
    }

    target.addEventListener('pointermove', handleMove)
    target.addEventListener('pointerup', finish)
    // `pointercancel` wird behandelt wie das Loslassen, nicht wie ein Abbruch:
    // Die Spalten stehen zu diesem Zeitpunkt bereits dort, wo der Zeiger sie
    // hingezogen hat. Sie zurückspringen zu lassen wäre die größere
    // Überraschung als das Festschreiben.
    target.addEventListener('pointercancel', finish)
  }, [releaseClass, settle, stopFollowing])

  return { pressed, startDrag }
}
